import React from "react";
import styled from "styled-components";
import { Modal, IconButton, CircularProgress } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { CloudDoneRounded } from "@material-ui/icons";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import { TIFFViewer } from "react-tiff";

const DocViewers = ({ modal, setModal }) => {
  if (!modal?.state || !modal?.url) return null;

  const fileName = modal.name || "Document";
  const ext =
    modal.format?.toLowerCase() ||
    modal.url.split(".").pop()?.toLowerCase() ||
    "";

  // Prevent caching
  const safeUrl = modal.url.includes("?")
    ? `${modal.url}&_=${Date.now()}`
    : `${modal.url}?_=${Date.now()}`;

  const isTiff = ["tif", "tiff"].includes(ext);
  const isMSOffice = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(
    ext,
  );

  const documents = [
    {
      uri: safeUrl,
      fileName: fileName,
      fileType: ext || undefined, // Let DocViewer detect if missing
    },
  ];

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = safeUrl;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal open={modal.state} onClose={() => setModal({ state: false })}>
      <PreviewContainer>
        <Header>
          <FileInfo>
            <FileDetails>
              <FileName title={fileName}>{fileName}</FileName>
              <FileFormat>{ext.toUpperCase()}</FileFormat>
            </FileDetails>
          </FileInfo>
          <div style={{ display: "flex", gap: "8px" }}>
            {" "}
            {modal?.checkStatus && (
              <IconButton
                onClick={handleDownload}
                title="Download"
                style={{ color: "white" }}
              >
                <CloudDoneRounded />
              </IconButton>
            )}
            <CloseButton
              onClick={() => setModal({ state: false })}
              title="Close"
            >
              <CloseIcon />
            </CloseButton>
          </div>
        </Header>

        <ViewerWrapper>
          {isTiff ? (
            <TiffWrapper>
              <TIFFViewer
                tiff={safeUrl}
                paginate="bottom"
                zoomable
                printable={false}
                style={{ width: "100%", height: "100%" }}
              />
            </TiffWrapper>
          ) : (
            <>
              <DocViewer
                documents={documents}
                pluginRenderers={DocViewerRenderers}
                config={{
                  header: {
                    disableHeader: true,
                    disableFileName: true,
                  },
                  loadingRenderer: {
                    overrideComponent: CustomLoading,
                  },
                  csvDelimiter: ",",
                  pdfZoom: {
                    defaultZoom: 1.1,
                    zoomJump: 0.2,
                  },
                }}
                style={{ width: "100%", height: "100%", background: "white" }}
                theme={{
                  primary: "#00838c",
                  secondary: "#f5f5f5",
                  tertiary: "#ffffff",
                  textPrimary: "#333",
                  textSecondary: "#666",
                  disableThemeScrollbar: false,
                }}
              />
              {isMSOffice && <FooterBlocker />}
            </>
          )}
        </ViewerWrapper>
      </PreviewContainer>
    </Modal>
  );
};

// Custom loading component
const CustomLoading = () => (
  <LoadingOverlay>
    <CircularProgress size={60} style={{ color: "#00838c" }} />
    <LoadingText>Loading document...</LoadingText>
  </LoadingOverlay>
);

export default DocViewers;

const PreviewContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 1200px;
  height: 90vh;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 25px 70px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  outline: none;
`;

const Header = styled.div`
  background: #00838c;
  color: white;
  padding: 14px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileDetails = styled.div`
  min-width: 0;
`;

const FileName = styled.div`
  font-size: 18px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileFormat = styled.span`
  font-size: 13px;
  opacity: 0.9;
`;

const CloseButton = styled(IconButton)`
  color: white !important;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ViewerWrapper = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  background: #f9f9f9;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.98);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  z-index: 10;
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: 16px;
  color: #555;
  font-weight: 500;
`;

const TiffWrapper = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  overflow: auto;
`;

const FooterBlocker = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50px;
  background: white;
  z-index: 10;
`;
